import {useMemo, useState} from 'react';
import useCustomLocation from '@/shared/hooks/useCustomLocation.hook';
import {useTranslation} from '@sdb/ui/translate/hooks/index';
import {camelToKebab} from '@/shared/utils/stringUtils';
import type {NavigationRailMenuDataItem} from '@sdb/ui/types/components/molecules/NavigationRail/NavigationRail.types';
import type {MenuViewModel} from '@/../generated/src/types';
import type {IconType} from '@sdb/ui/icons/utils/icon-types';

type RecursiveMenuItem = {
    label: string;
    href: string | null;
    subMenuItems?: RecursiveMenuItem[];
    iconType?: NonNullable<MenuViewModel['menuItems']>[number]['iconType'];
};

export const useNavigation = (menu: MenuViewModel | undefined) => {
    const {t} = useTranslation();
    const location = useCustomLocation();
    const currentPath = location.pathname;

    // Het laatst aangeklikte menu-item. Alleen van belang als dezelfde href op meer plekken in
    // het menu staat (weekoverzicht, #6844): dan blijft het item gemarkeerd waarop geklikt is,
    // of dat binnen de aangeklikte parent valt (een klik op Klassikaal opent zijn eerste kind).
    const [clickedId, setClickedId] = useState<string | null>(null);

    const navigate = (url: string) => {
        window.history.pushState({}, '', url);
        window.dispatchEvent(new CustomEvent('location-changed'));
    };

    const {navData, selectedId} = useMemo(() => {
        if (!menu?.menuItems) {
            return {navData: [], selectedId: null};
        }

        let currentSelectedId: string | null = null;
        // Ids van items wier eigen href bij het huidige pad hoort, met die href.
        const matchedHrefs = new Map<string, string>();
        const usedIds = new Set<string>();
        const parentIds = new Map<string, string>();

        // Een item mét kinderen klapt in de NavigationRail bij klik alleen open; het
        // anchor-href navigeert wél mee. Parents zonder eigen href krijgen daarom het href
        // van hun eerste kind, zodat één klik direct de eerste (voor deze gebruiker
        // zichtbare) pagina opent. Id en selectie blijven op het eigen href gebaseerd,
        // anders zouden parent en kind hetzelfde id krijgen.
        const firstLeafHref = (item: RecursiveMenuItem): string => {
            if (item.href) {
                return item.href;
            }
            for (const subItem of item.subMenuItems ?? []) {
                const href = firstLeafHref(subItem);
                if (href) {
                    return href;
                }
            }
            return '';
        };

        const matchesPath = (href: string) => {
            if (href === '/') {
                return currentPath === '/';
            }
            if (!href) return false;
            return currentPath === href || currentPath.startsWith(href + '/');
        };

        const hasSelectedDescendant = (item: RecursiveMenuItem): boolean =>
            (item.subMenuItems ?? []).some(
                (subItem) => matchesPath(subItem.href || '') || hasSelectedDescendant(subItem),
            );

        const mapMenuItems = (
            items: RecursiveMenuItem[],
            includeIcon = false,
            parentId = '',
        ): NavigationRailMenuDataItem[] =>
            items.map((item, idx) => {
                const itemHref = item.href || '';

                // compute a stable, unique id: prefer href, otherwise use a hierarchical fallback
                const fallbackSegment = item.label || `${idx}`;
                const baseId =
                    itemHref || (parentId ? `${parentId}/${fallbackSegment}` : fallbackSegment);
                // Staat dezelfde href al eerder in het menu, dan krijgt dit item een id op basis
                // van zijn parent; anders hebben twee items hetzelfde id en is de markering willekeurig.
                const itemId = usedIds.has(baseId) ? `${parentId}>${baseId}` : baseId;
                usedIds.add(itemId);
                parentIds.set(itemId, parentId);

                // map submenu first so children can set selection before parent
                const submenu =
                    item.subMenuItems && item.subMenuItems.length
                        ? mapMenuItems(item.subMenuItems, false, itemId)
                        : undefined;

                // If any child matched, currentSelectedId will already be set.
                if (itemHref && matchesPath(itemHref)) {
                    matchedHrefs.set(itemId, itemHref);
                    if (!currentSelectedId) {
                        // use the generated itemId so selection matches the nav item's id
                        currentSelectedId = itemId;
                    }
                }

                // Het afgeleide eerste-kind-href alleen zolang er géén kind van dit item
                // geselecteerd is: eenmaal in de sectie moet een klik op de parent weer
                // dichtklappen in plaats van opnieuw naar het eerste kind navigeren (#6509).
                const href = itemHref || (hasSelectedDescendant(item) ? '' : firstLeafHref(item));

                // De klik onthouden via onClickCapture op het anchor, niet via onClick: de rail
                // roept onClick niet aan voor een subitem mét kinderen (zoals Klassikaal), dat
                // alleen openklapt terwijl zijn afgeleide href wél navigeert. Zonder href
                // navigeert de klik niet, en blijft de markering waar hij was.
                const base: Partial<NavigationRailMenuDataItem> & {onClickCapture?: () => void} = {
                    id: itemId,
                    href: href || undefined,
                    label: t(item.label) || undefined,
                    submenu,
                    onClick: () => {},
                    onClickCapture: href ? () => setClickedId(itemId) : undefined,
                };

                if (includeIcon && item.iconType) {
                    base.iconType = camelToKebab(item.iconType) as IconType;
                }

                return base as NavigationRailMenuDataItem;
            });

        const navItems = mapMenuItems(menu.menuItems, true);

        const isWithinClicked = (id: string) => {
            for (let current: string | undefined = id; current; current = parentIds.get(current)) {
                if (current === clickedId) return true;
            }
            return false;
        };

        // Alleen een item met precies dezelfde href als de eerste treffer mag die vervangen,
        // en alleen als het aangeklikte item dat item zelf is of het bevat.
        const firstMatchHref = currentSelectedId ? matchedHrefs.get(currentSelectedId) : undefined;
        if (clickedId && firstMatchHref) {
            for (const [id, href] of matchedHrefs) {
                if (href === firstMatchHref && isWithinClicked(id)) {
                    currentSelectedId = id;
                    break;
                }
            }
        }

        return {navData: navItems, selectedId: currentSelectedId};
    }, [currentPath, menu, t, clickedId]);

    return {
        navData,
        selectedId,
        navigate,
    };
};
